import cx from 'classnames';
import { ComponentPropsWithoutRef, useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import styles from './EmojiFeedbackOption.module.css';

type ButtonProps = Pick<
  ComponentPropsWithoutRef<'button'>,
  'id' | 'disabled' | 'aria-label'
>;

export interface EmojiFeedbackOptionProps extends ButtonProps {
  className?: string;
  emoji?: string;
  label?: string;
  value: string;
  selected?: boolean;
  shouldAnimate?: boolean;
  onSelect?: (value: string) => void;
}

export default function EmojiFeedbackOption({
  id,
  className,
  emoji,
  label: labelText,
  value,
  selected,
  shouldAnimate,
  onSelect,
  ...otherProps
}: EmojiFeedbackOptionProps): JSX.Element {
  const handleClick = () => {
    onSelect?.(value);
  };

  const DURATION = shouldAnimate ? 0.4 : 0;
  const EASE = 'easeOut';
  const DELAY = 0;

  return (
    <motion.button
      onClick={handleClick}
      className={cx(
        styles['base'],
        {
          [styles['selected']]: selected,
        },
        className
      )}
      style={{
        height: selected ? '24px' : '96px',
        width: '96px',
      }}
      layout
      {...otherProps}
    >
      <motion.span
        className={styles['emoji']}
        animate={
          selected
            ? {
                fontSize: 'var(--u-text-large-font-size)',
                lineHeight: '24px',
                left: 0,
                top: 0,
              }
            : {}
        }
        transition={{
          duration: DURATION,
          ease: EASE,
          delay: DELAY,
        }}
        initial={{
          left: 'var(--u-spacing-24)',
          top: selected ? 'var(--u-spacing-32)' : 'var(--u-spacing-8)',
          fontSize: 'var(--u-text-colossal-font-size)',
          lineHeight: 'var(--u-text-colossal-line-height)',
        }}
      >
        {emoji}
      </motion.span>

      <motion.span
        className={styles['label-text']}
        initial={{
          left: '50%',
          transform: 'translateX(-50%)',
          top: 'var(--u-spacing-72)',
        }}
        animate={
          selected
            ? {
                left: 'var(--u-spacing-24)',
                top: 'var(--u-spacing-4)',
                marginTop: '0px',
                transform: 'translateX(0)',
              }
            : {}
        }
        transition={{ duration: DURATION, ease: EASE, delay: DELAY }}
      >
        {labelText}
      </motion.span>
    </motion.button>
  );
}
